import { useEffect, useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Point { t: string; v: number }
interface Series { series: string; points: Point[] }

interface BaseProps {
  width?: number
  height?: number
  className?: string
  strokeWidth?: number
  /** Show an end dot at the latest value. */
  showDot?: boolean
  /** Show the delta vs the earliest point as a small chip. */
  showDelta?: boolean
  /** Show percent change instead of absolute delta in the chip. */
  deltaPercent?: boolean
  /** Colour band: infers up/down colouring, or pass a specific Tailwind class. */
  tone?: 'auto' | 'up-good' | 'down-good' | 'blue' | 'rose' | 'emerald' | 'amber' | 'orange' | 'muted'
  /** Formatter for delta text. */
  format?: (v: number) => string
  /** Number of ticks to fetch / plot (only applied when using metric mode). */
  hours?: number
}

interface PointsMode extends BaseProps {
  points: { t?: string; v: number }[]
  metric?: never
  bulk?: never
}
interface MetricMode extends BaseProps {
  metric: string
  labels?: Record<string, string | number>
  points?: never
  bulk?: never
}
interface BulkSeriesMode extends BaseProps {
  bulkSeries: Series[]
  labels?: Record<string, string | number>
  metric?: never
  points?: never
}

type SparklineProps = PointsMode | MetricMode | BulkSeriesMode

// ── Hook: fetch a metric series on demand ────────────────────────────────────
function useSeries(metric: string | undefined, hours: number) {
  const [series, setSeries] = useState<Series[] | null>(null)
  useEffect(() => {
    if (!metric) return
    let cancelled = false
    api.get(`/api/trends/${encodeURIComponent(metric)}`, { params: { hours } })
      .then(r => { if (!cancelled) setSeries(r.data.series || []) })
      .catch(() => { if (!cancelled) setSeries([]) })
    return () => { cancelled = true }
  }, [metric, hours])
  return series
}

// ── Pick the series matching label filter from a bulk response ───────────────
function pickSeries(all: Series[] | null, labels?: Record<string, string | number>): Point[] {
  if (!all || all.length === 0) return []
  if (!labels || Object.keys(labels).length === 0) {
    // No filter — combine all series (rare; most metrics have one)
    return all[0].points
  }
  const target = Object.entries(labels).map(([k, v]) => `${k}=${v}`).sort().join(',')
  const match = all.find(s => s.series === target)
  return match ? match.points : []
}

function tonePath(tone: BaseProps['tone'], deltaRatio: number): string {
  if (tone === 'blue') return 'stroke-blue'
  if (tone === 'rose') return 'stroke-rose'
  if (tone === 'emerald') return 'stroke-emerald'
  if (tone === 'amber') return 'stroke-amber'
  if (tone === 'orange') return 'stroke-orange'
  if (tone === 'muted') return 'stroke-text-muted'
  if (tone === 'up-good') return deltaRatio > 0 ? 'stroke-emerald' : deltaRatio < 0 ? 'stroke-rose' : 'stroke-text-muted'
  if (tone === 'down-good') return deltaRatio > 0 ? 'stroke-rose' : deltaRatio < 0 ? 'stroke-emerald' : 'stroke-text-muted'
  // auto: default "up is good"
  return deltaRatio > 0 ? 'stroke-emerald' : deltaRatio < 0 ? 'stroke-rose' : 'stroke-text-muted'
}

function toneFill(tone: BaseProps['tone'], deltaRatio: number): string {
  if (tone === 'blue') return 'fill-blue'
  if (tone === 'rose') return 'fill-rose'
  if (tone === 'emerald') return 'fill-emerald'
  if (tone === 'amber') return 'fill-amber'
  if (tone === 'orange') return 'fill-orange'
  if (tone === 'muted') return 'fill-text-muted'
  if (tone === 'up-good') return deltaRatio > 0 ? 'fill-emerald' : deltaRatio < 0 ? 'fill-rose' : 'fill-text-muted'
  if (tone === 'down-good') return deltaRatio > 0 ? 'fill-rose' : deltaRatio < 0 ? 'fill-emerald' : 'fill-text-muted'
  return deltaRatio > 0 ? 'fill-emerald' : deltaRatio < 0 ? 'fill-rose' : 'fill-text-muted'
}

// ── Component ────────────────────────────────────────────────────────────────
export default function Sparkline(props: SparklineProps) {
  const {
    width = 80,
    height = 20,
    className,
    strokeWidth = 1.5,
    showDot = true,
    showDelta = false,
    deltaPercent = false,
    tone = 'auto',
    format,
    hours = 24,
  } = props

  const metric = 'metric' in props ? props.metric : undefined
  const bulkSeries = 'bulkSeries' in props ? props.bulkSeries : undefined
  const labels = 'labels' in props ? props.labels : undefined

  const fetched = useSeries(metric, hours)

  const pts: { t?: string; v: number }[] = useMemo(() => {
    if ('points' in props && props.points) return props.points
    if (bulkSeries) return pickSeries(bulkSeries, labels)
    if (fetched) return pickSeries(fetched, labels)
    return []
  }, [props, bulkSeries, fetched, labels])

  if (pts.length < 2) {
    return (
      <div className={clsx('inline-block', className)} style={{ width, height }}>
        <div className="w-full h-full border-b border-dashed border-border/50" />
      </div>
    )
  }

  const values = pts.map(p => p.v)
  const vMin = Math.min(...values)
  const vMax = Math.max(...values)
  const vRange = vMax - vMin || 1
  const xStep = pts.length > 1 ? width / (pts.length - 1) : width

  const path = pts
    .map((p, i) => {
      const x = i * xStep
      const y = height - ((p.v - vMin) / vRange) * (height - 2) - 1
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  const first = values[0]
  const last = values[values.length - 1]
  const absDelta = last - first
  const pctDelta = first !== 0 ? (absDelta / first) * 100 : 0
  const strokeClass = tonePath(tone, absDelta)
  const fillClass = toneFill(tone, absDelta)

  const lastX = (pts.length - 1) * xStep
  const lastY = height - ((last - vMin) / vRange) * (height - 2) - 1

  const deltaStr = format
    ? `${absDelta >= 0 ? '+' : ''}${format(absDelta)}`
    : deltaPercent
      ? `${pctDelta >= 0 ? '+' : ''}${pctDelta.toFixed(1)}%`
      : `${absDelta >= 0 ? '+' : ''}${Math.round(absDelta)}`

  return (
    <div className={clsx('inline-flex items-center gap-1.5', className)}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible"
        role="img"
        aria-label={`Sparkline ${metric || ''}`}
      >
        <path
          d={path}
          fill="none"
          className={clsx(strokeClass, 'opacity-90')}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {showDot && (
          <circle
            cx={lastX}
            cy={lastY}
            r={strokeWidth + 0.5}
            className={fillClass}
          />
        )}
      </svg>
      {showDelta && (
        <span className={clsx(
          'text-[9px] font-mono font-semibold',
          absDelta > 0 ? 'text-emerald' : absDelta < 0 ? 'text-rose' : 'text-text-muted'
        )}>
          {deltaStr}
        </span>
      )}
    </div>
  )
}
